import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { TrendingUp, Calendar } from 'lucide-react';
import AttendanceDialog from './AttendanceDialog';
import ParticipationDialog from './ParticipationDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TraineeManagement = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [showParticipationDialog, setShowParticipationDialog] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/students`);
      setStudents(response.data.students);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t('traineeManagement')}</h2>
        <p className="text-gray-600 mt-1">{t('registeredTrainees')}: {students.length}</p>
      </div>

      {/* Students List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {students.map((student) => (
          <Card key={student.id} className="shadow hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {student.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base truncate">{student.name}</CardTitle>
                  <p className="text-xs text-gray-500 truncate">{student.email}</p>
                </div>
                {/* Quick Attendance Indicator */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    (student.stats?.attendance_rate || 0) >= 70 
                      ? 'bg-green-100 text-green-700' 
                      : (student.stats?.attendance_rate || 0) >= 40 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {student.stats?.attendance_rate || 0}%
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Attendance Stats Row */}
                <div className="flex items-center justify-between text-xs bg-gray-50 rounded-lg p-2">
                  <div className="flex items-center gap-1 text-green-600">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span>حضور: {student.stats?.attendance_rate || 0}%</span>
                  </div>
                  <div className="flex items-center gap-1 text-red-600">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span>غياب: {student.stats?.absence_rate || 0}%</span>
                  </div>
                </div>

                {/* Participation Progress */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">{t('participationRate')}</span>
                    <span className="text-xs font-bold text-indigo-600">
                      {student.stats?.participation_rate || 0}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 transition-all"
                      style={{ width: `${student.stats?.participation_rate || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-1">
                  <Button
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-xs h-8"
                    size="sm"
                    onClick={() => {
                      setSelectedStudent(student);
                      setShowAttendanceDialog(true);
                    }}
                  >
                    <Calendar className="h-3.5 w-3.5 ml-1" />
                    الحضور
                  </Button>
                  <Button
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-xs h-8"
                    size="sm"
                    onClick={() => {
                      setSelectedStudent(student);
                      setShowParticipationDialog(true);
                    }}
                  >
                    <TrendingUp className="h-3.5 w-3.5 ml-1" />
                    المشاركة
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Attendance Dialog */}
      {showAttendanceDialog && selectedStudent && (
        <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
          <AttendanceDialog
            student={selectedStudent}
            onClose={() => setShowAttendanceDialog(false)}
            onUpdate={fetchStudents}
          />
        </Dialog>
      )}

      {/* Participation Dialog */}
      {showParticipationDialog && selectedStudent && (
        <Dialog open={showParticipationDialog} onOpenChange={setShowParticipationDialog}>
          <ParticipationDialog
            student={selectedStudent}
            onClose={() => setShowParticipationDialog(false)}
            onUpdate={fetchStudents}
          />
        </Dialog>
      )}
    </div>
  );
};

export default TraineeManagement;
